/*
  State model for synth subtest results, accessible from the test model via 'agentResults' property
  Subtest results are stored in an observable map of AgentTestResultsState objects, keyed by agent id.
  These individual models are further broken down to contain an observable map of test results per target (AgentTargetTestResultsState).
*/
import { action, computed, observable } from 'mobx';
import { isIpValid } from 'core/util/ip';
import moment from 'moment';
import { TEST_TYPES } from '@kentik/ui-shared/synthetics/constants';
import { DEFAULT_MESH_LOOKBACK, SYNGEST_CUTOVER } from 'app/util/constants';
import { parseQueryString } from 'app/util/utils';
import {
  getTimelinebounds,
  setLookbackFromHistory,
  testSupportsAgentDetails
} from 'app/views/synthetics/utils/syntheticsUtils';
import { getStartAndEndFromLookback } from 'core/util/dateUtils';
import { processTestResults, processTraceResults, aggregateSubtestResults } from './utils';
import AgentTestResultsState from './AgentTestResultsState';
import TestResultsState from './TestResultsState';

// the number of intervals we want to show as a trend of test results
// ex: a 1 hour lookback would be trended to the last 4 hours
const TREND_INTERVAL_SIZE = 4;

class AgentResultsState extends TestResultsState {
  get defaults() {
    return {
      ...super.defaults,
      agents: observable.map({}, { deep: false }),
      agentResults: null,
      loadingTraceResults: true,
      hasAgentResults: false,
      pageTabId: 'results',
      selectedResultTime: null,
      // trend results
      loadingTrendResults: false,
      loadingTrendDetailResults: false,
      hasTrendResults: false,
      trendResults: null,
      trendAggregationData: {},
      trendAgents: observable.map({}, { deep: false }),
      trendIntervals: new Array(TREND_INTERVAL_SIZE).fill(null).map(() => ({
        from: null,
        to: null,
        results: null
      })),
      selectedTrendDetailIndex: TREND_INTERVAL_SIZE - 1
    };
  }

  // an observable map of AgentTestResultsState objects, keyed by agent id
  @observable.ref
  agents = this.defaults.agents;

  // subtest results
  @observable.ref
  agentResults = this.defaults.agentResults;

  @observable
  hasAgentResults = this.defaults.hasAgentResults;

  // loading indicator representing the fetch for the entire trended query
  @observable
  loadingTrendResults = this.defaults.loadingTrendResults;

  // loading indicator used when fetching an individual interval in a trend query
  // after fetching, the interval results are cached
  @observable
  loadingTrendDetailResults = this.defaults.loadingTrendDetailResults;

  // indicates a valid set of trend results
  @observable
  hasTrendResults = this.defaults.hasTrendResults;

  // the currently selected timestamp in seconds
  @observable
  selectedResultTime = this.defaults.selectedResultTime;

  // the test results for the entire trend query
  @observable.ref
  trendResults = this.defaults.trendResults;

  // currently used to report on whether test results are aggregated
  @observable.ref
  trendAggregationData = this.defaults.trendAggregationData;

  // an observable map of AgentTestResultsState objects, keyed by agent id
  @observable.ref
  trendAgents = this.defaults.trendAgents;

  // an array sized by the number of supported intervals we want to report on in a trend
  // each entry will have a from/to date range and a place to cache results as they're loaded
  @observable.ref
  trendIntervals = this.defaults.trendIntervals;

  // the index of the currently selected trend interval
  @observable
  selectedTrendDetailIndex = this.defaults.selectedTrendDetailIndex;

  // map indicating previously loaded hashes
  loadedResultsHashes = {};

  configure({ startDate, endDate, lookbackSeconds, history, agent_id, target, force = false, ...displayOptions }) {
    const { test } = this;
    const { hasPing, hasBGPMonitoring, isPreview } = test;
    const isDns = test.get('test_type') === 'dns';

    // capture the export timestamp if passed in
    this.updateExportTime(history);

    if (agent_id) {
      this.setupAgentResults(agent_id, target);
    }

    if (!this.init || !this.hasLoadedResults(agent_id, target)) {
      Object.entries(displayOptions).forEach(([key, value]) => {
        if (this.displayOptions[key] !== undefined) {
          this.displayOptions[key] = value;
        }
      });

      const { usePageComponent, shared } = this.displayOptions;

      this.displayOptions.allowMesh = !isDns;
      this.displayOptions.allowSankey = !isDns;
      this.displayOptions.allowMap = !isDns || !this.store.$app.isExport;
      this.displayOptions.allowPath = hasPing;
      this.displayOptions.allowHealthTimeline = !isPreview;
      this.displayOptions.allowBgp = hasBGPMonitoring && usePageComponent && !isPreview;
      this.displayOptions.allowAgentDetails = !(shared || isPreview) && testSupportsAgentDetails(test);
      this.history = history;
      this.agent_id = agent_id;
      this.target = target;

      this.initiate({ startDate, endDate, lookbackSeconds });
    } else if (
      force ||
      (startDate && this.startDate !== startDate) ||
      (endDate && this.endDate !== endDate) ||
      (lookbackSeconds && this.lookbackSeconds !== lookbackSeconds)
    ) {
      this.onDateRangeChange({ startDate, endDate, lookbackSeconds });
    }
  }

  initiate({ startDate, endDate, lookbackSeconds }) {
    const { history, test } = this;
    const { $exports } = this.store;
    const { allowSettings } = this.displayOptions;
    const hasLocationHistory =
      this.checkForDateRange(history?.location?.state || {}) || parseQueryString(history?.location?.search).start;
    const hasBeenProvededADateRange = this.checkForDateRange({ startDate, endDate, lookbackSeconds });
    const defaultLookbackSeconds = this.test.isMesh ? DEFAULT_MESH_LOOKBACK : this.lookbackSeconds;
    let initPromise = Promise.resolve({
      startDate: this.startDate,
      endDate: this.endDate,
      lookbackSeconds: defaultLookbackSeconds
    });

    // we support to many ways to configure date range
    if (hasBeenProvededADateRange) {
      initPromise = Promise.resolve({ startDate, endDate, lookbackSeconds });
    } else if (hasLocationHistory && allowSettings) {
      initPromise = setLookbackFromHistory({ $exports, history, test }).then(() => null);
    }

    return initPromise
      .then((dateObj) => {
        if (allowSettings) {
          return $exports
            .getSettings()
            .then(({ hashedPageTabId, hashedLookbackSeconds, hashedStartDate, hashedEndDate }) => {
              if (hashedPageTabId !== undefined) {
                this.onPageTabChange(hashedPageTabId);
              }

              const hasDateRangeSettings = this.checkForDateRange({
                startDate: hashedStartDate,
                endDate: hashedEndDate,
                lookbackSeconds: hashedLookbackSeconds
              });

              if (hasDateRangeSettings && !hasBeenProvededADateRange) {
                return {
                  startDate: hashedStartDate,
                  endDate: hashedEndDate,
                  lookbackSeconds: hashedLookbackSeconds
                };
              }
              return dateObj;
            });
        }
        return dateObj;
      })
      .then((dateObj) => {
        this.onDateRangeChange(dateObj);
        this.init = true;
      });
  }

  // ----- fetching -------------------------------------

  // fetches fresh alarms, test results, trend test results, and trace results when the selected date changes
  fetch({ startDate, endDate, trendStartDate }) {
    // prevent flashes of empty state when executing fresh fetches
    // cache is reset on these fresh fetches so we're sure to have loading state managed correctly moving forward
    this.loadingTraceResults = true;

    this.newRequestID();
    this.buildTrendIntervals({ startDate: trendStartDate, endDate });
    this.fetchTestResults({ start_time: startDate, end_time: endDate }).then(() => this.fetchTraceResults());
    this.fetchTrendTestResults({ start_time: trendStartDate, end_time: endDate });
    this.fetchAlarmResults({
      start: moment.utc(startDate * 1000).toISOString(),
      end: moment.utc(endDate * 1000).toISOString()
    });
  }

  // based on the defined interval size, evenly divides into a list of date range arguments
  // we can use to jump between subtest results segments on the trend charts
  // these trend intervals also house cached results as they're loaded
  @action
  buildTrendIntervals({ startDate, endDate }) {
    // equally divide the time range by the number of trend intervals we want
    const intervalSize = Math.floor((endDate - startDate) / TREND_INTERVAL_SIZE);

    let fromTs = startDate;

    for (let i = 0; i < TREND_INTERVAL_SIZE; i++) {
      const from = fromTs;
      const fromMs = fromTs * 1000;
      const to = fromTs + intervalSize;
      const toMs = to * 1000;

      // populate the date range for the given interval
      this.trendIntervals[i] = {
        // from/to we'll use for test results queries
        from,
        to,
        // fromMs/toMs we'll use for chart plotting
        fromMs,
        toMs
      };

      // start the next interval where we left off
      fromTs += intervalSize;
    }
  }

  // post-processor for subtest results
  // sets data properties, manages loading indicators, and calculates min/max metrics for the ui
  processFetchTestResultsResponse(test, results) {
    const hasResults = this.checkForTestResults(results);

    if (hasResults) {
      const { health_ts = {} } = results;
      const tsKeys = Object.keys(health_ts);
      let ts = tsKeys[tsKeys.length - 1];
      const processedTestResults = processTestResults(test, results);
      const agentIds = Object.keys(processedTestResults);

      if (this.exportTime) {
        // override the selected timestamp when exporting
        // note that we want an explicit null value when we're exporting any results other than waterfall
        // this is so we don't litter the export with hovered tooltips and markers
        ts = this.selectedTabId === 'waterfall' ? this.exportTime : null;
      }

      this.agentResults = results;
      this.timestamps = Object.keys(results?.health_ts || {}).sort();
      this.selectedResultTime = +ts;
      this.resultTime = +ts;

      // strip out the avg aggregate results for processing
      const { results: aggregateResults, ...aggregationData } = results?.book?.aggregationData || {};

      // merge the subtest aggregates into a form that we can hydrate down the chain
      const aggregates = aggregateSubtestResults({
        aggregateResults,
        testResults: processedTestResults
      });

      // capture the max raw lookback and aggregation state of the syngest results
      this.aggregationData = aggregationData;

      for (let i = 0; i < agentIds.length; i += 1) {
        const agentId = agentIds[i];
        this.setupAgentResults(agentId);
        this.agents.get(agentId).setTestResults(results, processedTestResults[agentId], aggregates?.[agentId]);
      }
    }

    this.hasAgentResults = hasResults;
    this.loadingResults = false;
  }

  // see above - this does not do any aggregate calculations
  processFetchTrendTestResultsResponse(test, results) {
    const hasResults = this.checkForTestResults(results);
    if (hasResults) {
      const processedTestResults = processTestResults(test, results);
      const agentIds = Object.keys(processedTestResults);

      this.trendResults = results;

      // capture the max raw lookback and aggregation state of the syngest results
      this.trendAggregationData = results?.book?.aggregationData || {};

      for (let i = 0; i < agentIds.length; i += 1) {
        const agentId = agentIds[i];
        // sets up an AgentTestResultsState for the given agent if need be
        this.setupTrendAgentResults(agentId);

        // the AgentTestResultsState then sets the results as an AgentTargetTestResultsState
        // this final AgentTargetTestResultsState contains getters we use for things like httpHealthTs, pingHealthTs etc
        this.trendAgents.get(agentId).setTestResults(results, processedTestResults[agentId]);
      }
    }

    this.hasTrendResults = hasResults;
    this.loadingTrendResults = this.defaults.loadingTrendResults;
  }

  // fetch subtest results for the given date range
  @action
  fetchTestResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { isPreset } = this.test;

    // reset state
    this.agentResults = this.defaults.agentResults;
    this.timestamps = this.defaults.timestamps;
    this.resultTime = this.defaults.resultTime;
    this.aggregationData = this.defaults.aggregationData;
    this.hasAgentResults = this.defaults.hasAgentResults;
    this.loadingResults = true;

    // use 'withAggregation' to explicitly ask for avg aggregate results
    const req = { ...dateObj, ids: [test.id], withAggregation: true };
    let hash = test.id;
    if (this.agent_id) {
      req.agent_ids = [this.agent_id];
      hash += `.${this.agent_id}`;
    }

    /*
      Send targets in the syngest request for all test types except url, page load, and transaction.
      If we sent targets for url and page load, we would not have ping/knock task data returned.
      This is because the target for ping/knock task types chops down the original target to be just the hostname and would not match.
      Transaction tests do not send their targets because they would never match a task.

      It's particularly important to send in the target when fetching subtest results for a2a/mesh tests because:
        1. Instead of pulling all subtests for the given source agent, we will only pull the subtest for the source/target pair
        2. The aggregate average returned from syngest will be accurate
    */
    if (this.target && !test.isUrlDefined) {
      req.targets = [this.target];
      hash += `.${this.target}`;
    }

    return $syn.requests.fetchTestResults(req, { preset: isPreset }).then(
      action((resp) => {
        const results = resp.tests_health?.[test.id] || {};

        // check if request is relevant before storing response
        if (requestId === this.requestId) {
          this.processFetchTestResultsResponse(test, results);
          this.loadedResultsHashes[hash] = true;

          // cache the test result for trends to use
          this.trendIntervals[this.selectedTrendDetailIndex].results = results;
        }
      })
    );
  }

  // fetch subtest results for the given date range
  @action
  fetchTrendTestResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { isPreset } = this.test;

    // reset state
    this.trendResults = this.defaults.trendResults;
    this.trendAggregationData = this.defaults.trendAggregationData;
    this.hasTrendResults = this.defaults.hasTrendResults;
    this.selectedTrendDetailIndex = this.defaults.selectedTrendDetailIndex;
    this.loadingTrendResults = true;

    const req = { ...dateObj, ids: [test.id] };
    if (this.agent_id) {
      req.agent_ids = [this.agent_id];
    }

    // see fetchTestResults
    if (this.target && !test.isUrlDefined) {
      req.targets = [this.target];
    }

    $syn.requests.fetchTestResults(req, { preset: isPreset }).then(
      action((resp) => {
        const results = resp.tests_health?.[test.id] || {};

        // check if request is relevant before storing response
        if (requestId === this.requestId) {
          this.processFetchTrendTestResultsResponse(test, results);
        }
      })
    );
  }

  // loads detail subtest results when the trend selection changes
  // first checks the cache in the 'results' property in the trend intervals before fetching fresh
  @action
  fetchTrendDetailResults({ id }) {
    // protect against string poisioning
    const intervalId = +id;
    // mark the selected detail interval
    this.selectedTrendDetailIndex = intervalId;
    // get the details of the selected interval
    const { from, to, results } = this.trendIntervals[intervalId] || {};

    if (results) {
      // run the cache results through the processor chain
      this.processFetchTestResultsResponse(this.test, results);
    } else if (from && to) {
      // mark the trend detail sections as loading
      this.loadingTrendDetailResults = true;

      this.fetchTestResults({ start_time: from, end_time: to }).finally(
        // reset detail loading state
        action(() => (this.loadingTrendDetailResults = this.defaults.loadingTrendDetailResults))
      );
    }
  }

  // fetches trace results for the given test and selected date range
  // this hydrates the path panel
  @action
  fetchTraceResults() {
    const { $syn } = this.store;
    const { requestId, test, agent_id, target } = this;
    const { id, isPreset } = test;
    const { allowPath } = this.displayOptions;

    if (allowPath) {
      // reset state
      this.loadingTraceResults = true;

      const req = { ...this.traceQueryTimeRange, id };
      if (agent_id) {
        req.agent_ids = [`${agent_id}`];
      }

      // url-defined and hostname tests resolve to multiple ips (e.g. v4 and v6), so don't pin traces to one
      if (target && isIpValid(target) && !test.isUrlDefined && !test.isHostname) {
        req.target_ips = [target];
      }

      if (this.targetAgentId) {
        req.target_agent_ids = [this.targetAgentId];
      }

      // capture the params used to fetch trace results
      // agentIds and targetIps are used to fetch the results
      // allTargets and selectedTargets are returned from the fetch and used to set the selected state
      this.lastFetchedTraceParams = {
        ...this.lastFetchedTraceParams,
        agentIds: req.agent_ids,
        targetIps: req.target_ips
      };

      $syn.requests
        .fetchTestTraceroutee(id, req, { preset: isPreset })
        .then(
          action((traceResults) => {
            // check if request is relevant before storing response
            if (requestId === this.requestId) {
              // cache the results
              this.traceResultsTs[this.selectedResultTime] = traceResults;

              // capture the selected target ips from the last fetch
              // set both to the selected target ips so we have a '1 of 1' selected state in this subtest
              // this is what we'd always expect in a subtest result
              this.lastFetchedTraceParams.allTargets = traceResults?.selected_targets || [];
              this.lastFetchedTraceParams.selectedTargets = traceResults?.selected_targets || [];

              this.loadingTraceResults = false;

              this.processAgentTraceResults();
            }
          })
        )
        .catch(
          action(() => {
            // reset state on error so we at least remove loading state and can display empty results
            this.lastFetchedTraceParams = this.defaults.lastFetchedTraceParams;
            this.loadingTraceResults = false;
          })
        );
    }
  }

  // returns a filter for querying against the alert manager alarms api
  // a2a/mesh tests can define a filter based on the target agent id for anything other than per-test alerting
  getAlarmFetchFilter() {
    const { test } = this;

    if (!test.isPerTestAlerting && (test.isMesh || test.isAgentTest)) {
      return {
        keys: {
          filter: {
            targetAgentId: {
              equals: this.target
            }
          }
        }
      };
    }

    return {};
  }

  // fetches alarm results for the given source/target and date range
  @action
  fetchAlarmResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;

    // reset state
    this.alarmResults = this.defaults.alarmResults;
    this.hasAlarmResults = this.defaults.hasAlarmResults;
    this.loadingAlarmResults = true;

    return $syn.requests
      .getTestAlarms({ ...dateObj, ids: [test.id], filter: this.getAlarmFetchFilter() }, { preset: test.isPreset })
      .then(
        action((resp) => {
          // check if request is relevant before storing response
          if (requestId === this.requestId) {
            if (resp?.alarms) {
              // filter results to ensure the alarm timeline is populated accurately
              resp.alarms = resp.alarms.filter((alarm) => {
                const { issueTargetsByAgentId = {} } = alarm?.details?.groupData || {};
                const issueTargets = issueTargetsByAgentId[this.agent_id];

                if (issueTargets?.length > 0) {
                  // a2a tests always have the same target
                  // mesh tests that are not per-test have already had their targets filtered via api query (see getAlarmFetchFilter above)
                  // transaction tests don't have a target to match against
                  // everyone else should at least be trying to match against the configured target
                  const matchesTarget =
                    test.isAgentTest ||
                    (!test.isPerTestAlerting && test.isMesh) ||
                    test.isTransaction ||
                    issueTargets.includes(this.target);

                  if (test.isPerTestAlerting && test.isMesh) {
                    // per-test alerting types need to search by ip since they don't have a key in the alarm
                    const targetAgentModel = $syn.agents.get(this.target);
                    return matchesTarget || (targetAgentModel?.ips || []).find((ip) => issueTargets.includes(ip));
                  }

                  if (test.isDns) {
                    // dns targets come in as ips so we need to also put in a match here for the target hostname
                    return matchesTarget || issueTargets.includes(test.get('config.target.value'));
                  }

                  if ([TEST_TYPES.URL, TEST_TYPES.PAGE_LOAD].includes(test.get('test_type'))) {
                    // http and page load ping/trace tasks target just the hostname so a check for that
                    return matchesTarget || issueTargets.includes(URL.parse(this.target)?.hostname);
                  }

                  return matchesTarget;
                }

                return false;
              });
            }

            this.alarmResults = resp;
            this.hasAlarmResults = !!resp?.alarms.length;
            this.loadingAlarmResults = false;
          }
        })
      )
      .catch(
        action((e) => {
          this.alarmResults = this.defaults.alarmResults;
          this.hasAlarmResults = this.defaults.hasAlarmResults;
          this.loadingAlarmResults = false;

          console.error(e);
        })
      );
  }

  // initializes an AgentTestResultsState for the given agent id and AgentTargetTestResultsState for the given target if necessary
  @action
  setupAgentResults(id, target) {
    const { $syn } = this.store;

    if (!this.agents.get(id)) {
      this.agents.set(
        id,
        new AgentTestResultsState({
          store: this.store,
          test: this.test,
          agent: $syn.agents.get(id)
        })
      );
    }

    if (target) {
      this.agents.get(id).setupTargetResults(target);
    }
  }

  // see above, but for trend results
  @action
  setupTrendAgentResults(id, target) {
    const { $syn } = this.store;

    if (!this.trendAgents.get(id)) {
      this.trendAgents.set(
        id,
        new AgentTestResultsState({
          store: this.store,
          test: this.test,
          agent: $syn.agents.get(id)
        })
      );
    }

    if (target) {
      this.trendAgents.get(id).setupTargetResults(target);
    }
  }

  // allow loading of subtest results without the need to run through the entire fetch cycle, with ai advisor being the primary use case
  @action
  loadResults({ agent_id, target, start, end, timestamps, results = [] } = {}) {
    const [subtestResults] = results;

    // initialize properties normally handled during the 'configure' stage
    this.agent_id = agent_id;
    this.target = target;

    // set the timestamp range
    this.timestamps = timestamps;

    if (start && end) {
      // fetch alarms
      this.fetchAlarmResults({
        start: moment.unix(start).toISOString(),
        end: moment.unix(end).toISOString()
      });
    }

    this.processFetchTestResultsResponse(this.test, subtestResults?.tests_health || {});
  }

  // ----- computed properties -------------------------------------

  // override to return subtest results
  @computed
  get results() {
    if (this.loadingResults) {
      return null;
    }

    return this.agentResults;
  }

  // another override to remain compliant with parent class (see above)
  @computed
  get hasResults() {
    return this.hasAgentResults;
  }

  // returns the trace results at the currently selected timestamp
  @computed
  get traceResults() {
    const { selectedResultTime, traceResultsTs, loadingTraceResults } = this;

    if (loadingTraceResults) {
      return {};
    }

    return traceResultsTs[selectedResultTime] || {};
  }

  // provides key properties for the rendering of trend charts
  @computed
  get trendTimelineBounds() {
    const { hasTrendResults, trendResults } = this;

    if (hasTrendResults) {
      // for the trend bounds, we want to ignore the adjustment ('true' arg) and just fill to the ends with what we have
      const { xMin, xMax, xAxisMin, xAxisMax, delta } = getTimelinebounds(trendResults.health_ts, true);

      return { xMin, xMax, xAxisMin, xAxisMax, delta };
    }

    return {};
  }

  // the currently selected tab id in the subtest
  @computed
  get selectedTabId() {
    return this.agents.get(this.agent_id)?.tabId;
  }

  // the currently selected timestamp converted to milliseconds
  @computed
  get selectedResultTimeMs() {
    const { selectedResultTime } = this;

    return selectedResultTime ? selectedResultTime * 1000 : null;
  }

  @computed
  get targetAgentId() {
    const { target, test } = this;
    const { $syn } = this.store;

    // For mesh and agent tests, we want to set the targetAgentId to be used when processing the trace results
    if (target && (test.isMesh || test.isAgentTest)) {
      return isIpValid(target) ? $syn.agents.getTargetAgentsByIps([target])?.[0]?.id : target;
    }

    return null;
  }

  @computed
  get targetHostname() {
    const { test } = this;

    // For hostname tests, or url based tests, that have trace results, we need the target to be the hostname, because ips change
    if (test.isHostname || test.isUrlDefined) {
      return test.agentResults.target;
    }

    return null;
  }

  @computed
  get traceQueryTimeRange() {
    const { selectedResultTime } = this;

    return {
      start_time: selectedResultTime,
      end_time: selectedResultTime + this.traceQueryOffset
    };
  }

  // ----- class utils -------------------------------------

  // checks the subtest and trace results hash for fetch logic
  hasLoadedResults(agent_id, target) {
    const test_id = this.test.id;
    const hashmap = this.loadedResultsHashes;
    if (hashmap[`${test_id}`]) {
      return true;
    }
    if (agent_id && hashmap[`${test_id}.${agent_id}`]) {
      return true;
    }
    if (agent_id && target && hashmap[`${test_id}.${agent_id}.${target}`]) {
      return true;
    }
    return false;
  }

  // determines whether a trend interval is currently selected
  isTrendDetailSelected({ id }) {
    return this.selectedTrendDetailIndex === +id;
  }

  // resets the hashes for subtest and trace results
  resetLoadState() {
    this.loadedResultsHashes = {};
  }

  processAgentTraceResults() {
    const { traceResults, targetAgentId, targetHostname } = this;
    const { $syn } = this.store;
    const hasTraceResults = this.checkForTraceResults(traceResults);

    if (hasTraceResults) {
      const processedTraceResults = processTraceResults(traceResults, targetAgentId, targetHostname, $syn);
      const agentIds = Object.keys(processedTraceResults);

      for (let i = 0; i < agentIds.length; i += 1) {
        const agentId = agentIds[i];
        this.setupAgentResults(agentId);
        this.agents.get(agentId).setTraceResults(traceResults, processedTraceResults[agentId]);
      }
    } else {
      // clear out all the trace results
      this.agents.forEach((agentTestState) => agentTestState.setTraceResults());
    }
  }

  // ----- change handlers -------------------------------------

  // processes a date selection change resulting in a fresh fetch of data
  // override from the parent to account for trend fetch logic
  onDateRangeChange({ lookbackSeconds, startDate, endDate }) {
    const { displayOptions, history } = this;
    const { $auth } = this.store;
    const dateObj = this.enforceMinLookback({ lookbackSeconds, startDate, endDate });
    const v1 = !!history?.location.pathname.includes('/v1');

    if (displayOptions.usePageComponent && !$auth.hasSudo) {
      if (v1) {
        if (!dateObj.startDate || dateObj.startDate > SYNGEST_CUTOVER) {
          history.push(`${history.location.pathname.replace('/v1', '')}`, dateObj);
        }
      } else if (dateObj.startDate && dateObj.startDate < SYNGEST_CUTOVER) {
        history.push(`${history.location.pathname}/v1`, dateObj);
      }
    }

    // don't use lookbackSeconds, does not align well across services
    if (lookbackSeconds) {
      const { start, end } = getStartAndEndFromLookback(lookbackSeconds, 120);
      dateObj.actualStartDate = start.unix();
      dateObj.actualEndDate = end.unix();
    } else {
      dateObj.actualStartDate = dateObj.startDate;
      dateObj.actualEndDate = dateObj.endDate;
    }

    // get the trend start date based on interval size
    const trendDetailIntervalLength = dateObj.actualEndDate - dateObj.actualStartDate;
    const trendStartDate = dateObj.actualStartDate - trendDetailIntervalLength * (TREND_INTERVAL_SIZE - 1);

    dateObj.trendStartDate = trendStartDate;

    this.setDateRange(dateObj);
    this.resetLoadState();
    this.fetch({
      startDate: dateObj.actualStartDate,
      endDate: dateObj.actualEndDate,
      trendStartDate: dateObj.trendStartDate
    });

    if (displayOptions.allowSettings) {
      this.store.$exports.setHash(
        {
          hashedLookbackSeconds: dateObj.lookbackSeconds,
          hashedStartDate: dateObj.startDate,
          hashedEndDate: dateObj.endDate
        },
        true,
        true
      ); // merge and replace the querystring to just be the q hash
    }
  }

  // ensures chart selections and tooltips show and follow each other when hovering in subtest results
  @action
  onChangeResultTimeMs(resultTimeMs) {
    this.resultTime = resultTimeMs / 1000;
  }

  // handles timestamp selection in the alarm timeline and fetches corresponding trace results for the subtest results path panel
  @action
  onChangeSelectedResultTimeMs(selectedResultTimeMs) {
    this.selectedResultTime = selectedResultTimeMs / 1000;
    this.fetchTraceResults();
  }
}

export default AgentResultsState;
